/**
 * Smooth scroll (Lenis) — Borie de Maurel
 *
 * MAJ thème du 16/10 : à partir de 990px, le thème ne fait plus défiler la fenêtre.
 * html/body sont en `height: 100dvh; overflow: hidden` et c'est `.page-wrapper`
 * (overflow-y: auto) qui défile. Lenis doit donc piloter `.page-wrapper` sur desktop,
 * et la fenêtre en dessous de 990px (où le document défile normalement).
 * Sinon Lenis bloque la molette en essayant de faire défiler une fenêtre figée.
 *
 * Lenis pilote la position de scroll *native* du conteneur : `position: sticky`,
 * ancres et écouteurs de scroll du header continuent de fonctionner.
 *
 * Exclusions (gestion propre du scroll par le thème) :
 *   - dialog.js pose `scroll-lock` sur <html> (modales, panier, recherche) → Lenis s'arrête.
 *   - drag-zoom-wrapper (carte interactive) consomme la molette.
 *   - media-gallery (galerie produit) a son propre rail vertical.
 *
 * Écrans tactiles sans souris (téléphones, tablettes) : Lenis n'agit pas sur le doigt
 * (syncTouch: false), il n'est donc pas lancé du tout (01/11/2026, moins de travail JS au
 * chargement sur mobile). Il démarre si une souris ou un pavé tactile est branché.
 *
 * <slideshow-component> n'est volontairement PAS exclu (mini-carrousels des cartes
 * produit en scroll-snap horizontal qui figeaient le scroll de page au survol).
 */
(function () {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (prefersReducedMotion.matches) return;
  if (typeof Lenis === 'undefined') return;

  const OPT_OUT = [
    'drag-zoom-wrapper',
    'media-gallery',
    'zoom-dialog',
    'dialog-component',
    'cart-drawer-component',
    'predictive-search-component',
    '[data-lenis-prevent]',
  ].join(',');

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const finePointerQuery = window.matchMedia('(any-pointer: fine)');

  let lenis = null;
  let rafId = 0;
  let resizeObserver = null;

  /* Renvoie .page-wrapper quand c'est lui le conteneur qui défile, sinon null (= fenêtre). */
  function getScrollWrapper() {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (!pageWrapper || !desktopQuery.matches) return null;
    const overflowY = window.getComputedStyle(pageWrapper).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll' ? pageWrapper : null;
  }

  function syncScrollLock() {
    if (!lenis) return;
    if (document.documentElement.hasAttribute('scroll-lock')) {
      lenis.stop();
    } else {
      lenis.start();
    }
  }

  function destroy() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    if (lenis) {
      lenis.destroy();
      lenis = null;
    }
    window.bdmLenis = null;
  }

  function create() {
    destroy();
    if (!finePointerQuery.matches) return;

    const wrapper = getScrollWrapper();
    const options = {
      duration: 1.3,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo out
      smoothWheel: true,
      wheelMultiplier: 1,
      syncTouch: false,
      prevent: (node) => node instanceof Element && node.closest(OPT_OUT) !== null,
    };

    if (wrapper) {
      options.wrapper = wrapper;
      options.content = wrapper;
      options.eventsTarget = wrapper;
    }

    lenis = new Lenis(options);

    /* Le conteneur garde une hauteur fixe (100dvh) : on recalcule la limite de scroll
       quand le contenu change de taille (images lazy, sections dynamiques). */
    if (wrapper && typeof lenis.resize === 'function' && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        if (lenis) lenis.resize();
      });
      Array.from(wrapper.children).forEach((child) => resizeObserver.observe(child));
    }

    const raf = (time) => {
      if (!lenis) return;
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    syncScrollLock();
    window.bdmLenis = lenis;
  }

  new MutationObserver(syncScrollLock).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['scroll-lock'],
  });

  /* Passage desktop <-> tablette/mobile : le conteneur qui défile change. */
  desktopQuery.addEventListener('change', create);
  finePointerQuery.addEventListener('change', create);

  prefersReducedMotion.addEventListener('change', (event) => {
    if (event.matches) destroy();
  });

  create();
})();
